import {
  type StudySwimlaneSelectDto,
  type StudyThreadSelectDto,
  StudyThreadSelectDtoKillChainCategoryEnum,
  SwimlaneMeasuresStatus,
} from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { Tile } from '@components/shared/tile.component';
import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@lib/utils';
import { useGetMeiaThreadSwimlanes } from '@queries/meia.query';
import { CheckCircle2Icon, ChevronRightIcon, CircleAlertIcon, CircleSlashIcon, PlusIcon } from 'lucide-react';
import { type FC, Fragment, type ReactNode, useMemo, useState } from 'react';
import { MeiaAddCustomSwimlaneModal } from './meia-add-custom-swimlane-modal.component';
import { MeiaThreadMeasuresTiles } from './meia-thread-measures-tiles.component';
import { MeiaThreadPatternStrip } from './meia-thread-pattern-strip.component';
import { MeiaThreadStageModal } from './meia-thread-stage-modal.component';

type MeiaThreadCardProps = {
  canEdit: boolean;
  missionId: string;
  studyId: string;
  thread: StudyThreadSelectDto;
};

const MEASURES_STATUS_TOOLTIP: Record<SwimlaneMeasuresStatus, string> = {
  [SwimlaneMeasuresStatus.COMPLETE]: 'Every MoS on this Stage has at least one MoE.',
  [SwimlaneMeasuresStatus.EMPTY]: 'This Stage has no MoS yet.',
  [SwimlaneMeasuresStatus.PARTIAL]: 'At least one MoS on this Stage is missing a MoE.',
};

export const MeiaThreadCard: FC<MeiaThreadCardProps> = (props) => {
  const { canEdit, missionId, studyId, thread } = props;
  const isCustomThread = thread.killChainCategory === StudyThreadSelectDtoKillChainCategoryEnum.CUSTOM;
  const { data: swimlanes = [], isLoading } = useGetMeiaThreadSwimlanes({ studyId, threadId: thread.id });

  const orderedSwimlanes = useMemo(() => [...swimlanes].sort((a, b) => a.position - b.position), [swimlanes]);
  const [editingSwimlane, setEditingSwimlane] = useState<StudySwimlaneSelectDto | null>(null);
  const [isAddingCustomStage, setIsAddingCustomStage] = useState(false);
  const nextCustomStagePosition =
    orderedSwimlanes.length === 0 ? 0 : Math.max(...orderedSwimlanes.map((swimlane) => swimlane.position)) + 1;
  const handleAddCustomSwimlane = canEdit && isCustomThread ? () => setIsAddingCustomStage(true) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <MeiaThreadPatternStrip
        canEdit={canEdit}
        customStageCount={isCustomThread ? orderedSwimlanes.length : 0}
        missionId={missionId}
        studyId={studyId}
        thread={thread}
      />

      <Tile
        contentClassName="gap-3"
        header={{
          title: (
            <span className="inline-flex items-center gap-2">
              <span>Thread Stages</span>
              <span className="text-xs font-normal text-gray-400">({orderedSwimlanes.length})</span>
            </span>
          ),
        }}
      >
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : orderedSwimlanes.length === 0 ? (
          isCustomThread ? (
            <EmptyStateContainer
              message={handleAddCustomSwimlane ? '+ Add Stage' : undefined}
              onClick={handleAddCustomSwimlane}
              title="No Stages"
            />
          ) : (
            <div className="flex flex-col items-start gap-2 rounded-md border border-gray-200 bg-white px-3 py-4">
              <p className="text-xs text-gray-500">
                Stages have not been seeded yet. Re-create the Thread to seed Stages from the kill-chain template.
              </p>
            </div>
          )
        ) : (
          <SwimlaneTabs
            canAddCustomStage={Boolean(handleAddCustomSwimlane)}
            onAddCustomSwimlane={handleAddCustomSwimlane}
            onSelect={setEditingSwimlane}
            swimlanes={orderedSwimlanes}
          />
        )}
      </Tile>

      {orderedSwimlanes.length > 0 && (
        <MeiaThreadMeasuresTiles
          missionId={missionId}
          studyId={studyId}
          swimlanes={orderedSwimlanes}
        />
      )}

      {editingSwimlane && (
        <MeiaThreadStageModal
          canEdit={canEdit}
          isOpen={Boolean(editingSwimlane)}
          missionId={missionId}
          onClose={() => setEditingSwimlane(null)}
          studyId={studyId}
          swimlane={editingSwimlane}
          thread={thread}
        />
      )}

      {isAddingCustomStage && (
        <MeiaAddCustomSwimlaneModal
          isOpen={isAddingCustomStage}
          onClose={() => setIsAddingCustomStage(false)}
          position={nextCustomStagePosition}
          studyId={studyId}
          threadId={thread.id}
        />
      )}
    </div>
  );
};

type SwimlaneTabsProps = {
  canAddCustomStage?: boolean;
  onAddCustomSwimlane?: () => void;
  onSelect: (swimlane: StudySwimlaneSelectDto) => void;
  swimlanes: StudySwimlaneSelectDto[];
};

const SwimlaneTabs: FC<SwimlaneTabsProps> = (props) => {
  const { canAddCustomStage, onAddCustomSwimlane, onSelect, swimlanes } = props;
  return (
    <div className="flex items-stretch gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:thin]">
      {swimlanes.map((swimlane, index) => (
        <Fragment key={swimlane.id}>
          <SwimlaneTab
            onSelect={() => onSelect(swimlane)}
            ordinal={index + 1}
            swimlane={swimlane}
          />
          {index < swimlanes.length - 1 && (
            <div
              aria-hidden
              className="flex shrink-0 items-center"
            >
              <ChevronRightIcon className="size-4 text-gray-300" />
            </div>
          )}
        </Fragment>
      ))}
      {canAddCustomStage && onAddCustomSwimlane && (
        <>
          {swimlanes.length > 0 && (
            <div
              aria-hidden
              className="flex shrink-0 items-center"
            >
              <ChevronRightIcon className="size-4 text-gray-300" />
            </div>
          )}
          <AddStageCard onClick={onAddCustomSwimlane} />
        </>
      )}
    </div>
  );
};

type AddStageCardProps = {
  onClick: () => void;
};

const AddStageCard: FC<AddStageCardProps> = (props) => {
  const { onClick } = props;
  return (
    <button
      className="flex w-[220px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-gray-200 bg-white p-3 text-gray-500 transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
      onClick={onClick}
      type="button"
    >
      <PlusIcon className="size-5" />
      <span className="text-sm font-medium">Add Stage</span>
    </button>
  );
};

type SwimlaneTabProps = {
  onSelect: () => void;
  ordinal: number;
  swimlane: StudySwimlaneSelectDto;
};

const STATUS_ICON: Record<SwimlaneMeasuresStatus, { Icon: typeof CheckCircle2Icon; className: string }> = {
  [SwimlaneMeasuresStatus.COMPLETE]: { Icon: CheckCircle2Icon, className: 'text-emerald-600' },
  [SwimlaneMeasuresStatus.EMPTY]: { Icon: CircleSlashIcon, className: 'text-gray-400' },
  [SwimlaneMeasuresStatus.PARTIAL]: { Icon: CircleAlertIcon, className: 'text-amber-500' },
};

const SwimlaneTab: FC<SwimlaneTabProps> = (props) => {
  const { onSelect, ordinal, swimlane } = props;
  const { Icon, className: iconClass } =
    STATUS_ICON[swimlane.measuresStatus] ?? STATUS_ICON[SwimlaneMeasuresStatus.EMPTY];

  return (
    <button
      className="relative flex w-[220px] shrink-0 cursor-pointer flex-col rounded-md border border-gray-200 bg-white p-3 text-left text-gray-700 transition-all hover:border-primary hover:bg-gray-50"
      onClick={onSelect}
      type="button"
    >
      <BlueLabel
        className="mb-1 text-[10px] leading-none"
        text={`Stage ${String(ordinal).padStart(2, '0')}`}
      />
      <div className="flex items-start gap-2">
        <TooltipComponent
          side="top"
          trigger={
            <span
              aria-label={`Measures status: ${swimlane.measuresStatus.toLowerCase()}`}
              className="mt-0.5 inline-flex shrink-0"
              onClick={(event) => event.stopPropagation()}
            >
              <Icon className={cn('size-5', iconClass)} />
            </span>
          }
        >
          {MEASURES_STATUS_TOOLTIP[swimlane.measuresStatus]}
        </TooltipComponent>
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="text-sm font-semibold leading-tight">{swimlane.phaseName}</span>
          <span className="line-clamp-2 min-h-[28px] text-[11px] font-normal leading-tight text-gray-500">
            {swimlane.operationalPurpose?.trim() ? swimlane.operationalPurpose : 'No description yet.'}
          </span>
        </div>
      </div>
      <div className="mt-3 flex items-stretch gap-1.5">
        <SwimlaneCountChip
          count={swimlane.mosCount ?? 0}
          label="MoS"
        />
        <SwimlaneCountChip
          count={swimlane.moeCount ?? 0}
          label="MoE"
        />
      </div>
    </button>
  );
};

type SwimlaneCountChipProps = {
  count: number;
  label: ReactNode;
};

const SwimlaneCountChip: FC<SwimlaneCountChipProps> = (props) => {
  const { count, label } = props;
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5 rounded-md border border-gray-200 bg-white px-2 py-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-800">{count}</span>
    </div>
  );
};
